import { Button } from '@thanx/uikit/button'
import palette from 'constants/palette'
import React from 'react'
import { useStyletron } from 'styletron-react'

export type SortDirection = 'asc' | 'desc' | null

type Props = {
  label: string // pre-translated by the calling scene
  sortAriaLabel: string // pre-translated, e.g. "Sort by date"
  direction: SortDirection
  onSort: () => void
  className?: string // the enclosing table's header-cell class
  align?: 'left' | 'right' // matches the column's cell alignment; default left
}

const HEADER_FONT_SIZE_PX = 14

// aria-sort reflects the visual sort state. Written as an early-return lookup, not
// a 3-branch ternary (a review rejection).
const ariaSortFor = (
  direction: SortDirection
): 'ascending' | 'descending' | 'none' => {
  if (direction === 'asc') return 'ascending'
  if (direction === 'desc') return 'descending'
  return 'none'
}

const SortableColumnHeader = ({
  label,
  sortAriaLabel,
  direction,
  onSort,
  className,
  align = 'left',
}: Props) => {
  const [css] = useStyletron()

  return (
    <th scope="col" aria-sort={ariaSortFor(direction)} className={className}>
      <Button
        kind="minimal"
        aria-label={sortAriaLabel}
        onClick={onSort}
        overrides={{
          // The enclosing <th> already owns the cell padding, so trim the minimal
          // button flush and restate the header typography. overrides.BaseButton
          // REPLACES uikit's kind styling rather than merging (uikit spreads the
          // consumer override last), so the reset lives entirely here — and padding
          // is longhand because uikit's BaseButton uses longhand and mixing it with
          // a `padding` shorthand trips Styletron's atomic-render warning. width:
          // 100% lets the inner span's justify-content:space-between push the
          // caret to the th's right padding edge (Figma TUTD spec), not glued to
          // the label.
          BaseButton: {
            style: {
              width: '100%',
              paddingTop: 0,
              paddingRight: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              fontWeight: 700,
              fontSize: `${HEADER_FONT_SIZE_PX}px`,
              color: palette.grey90,
              backgroundColor: 'transparent',
              ':hover': { backgroundColor: 'transparent' },
            },
          },
        }}
      >
        <span
          className={`d-flex align-items-center ${css({
            justifyContent: align === 'right' ? 'flex-end' : 'space-between',
            gap: align === 'right' ? '8px' : undefined,
            width: '100%',
          })}`}
        >
          {label}
          {/* Figma always shows the stacked double caret (fa-sort), even in a
              sorted state — only aria-sort/the aria-label convey direction. */}
          <i
            className={`fa fa-sort ${css({
              color: direction !== null ? palette.grey90 : palette.grey50,
            })}`}
            aria-hidden="true"
          />
        </span>
      </Button>
    </th>
  )
}

export default SortableColumnHeader
